import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CarRentalOutlinedIcon from '@mui/icons-material/CarRentalOutlined';
import CarRepairIcon from "@mui/icons-material/CarRepair";
import HomeOutlined from "@mui/icons-material/HomeOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";

import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import KeyboardReturnOutlinedIcon from '@mui/icons-material/KeyboardReturnOutlined';
import {
  AccountBalanceWallet,
  FireTruck,
  LocalShipping,
  TakeoutDining,
} from "@mui/icons-material";
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';

import CommuteIcon from "@mui/icons-material/Commute";
import DoneAllIcon from '@mui/icons-material/DoneAll';
import EngineeringIcon from "@mui/icons-material/Engineering";
import GppGoodIcon from "@mui/icons-material/GppGood";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import {
  Box,
  Drawer,
  List,
  useMediaQuery,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { PermissionContext } from "../PermissionContext";
import DepartmentSidebarFolders from "./DepartmentSidebarFolders";
import { DEPARTMENTS_CHANGED_EVENT, departmentHref, departmentIdFromLocation, hasLaneAccess } from "../utils/procurementMode";
import { canAccessDepartmentLane } from "../utils/departmentAccess";
import {
  SETTINGS_ACCESS_PERMISSIONS,
  hasAnyPermission,
} from "../utils/permissions";
import {
  getCompanyCode,
  getRole,
  getUsername,
  hasSession,
  redirectToLogin,
} from "../auth/authSession";
import { DRAWER_WIDTH, HEADER_HEIGHT } from "../theme";
import {
  CATEGORY_ACCENT,
  SIDEBAR,
  categoryIconWellSx,
  expandedRailSx,
  moduleIconSx,
  sectionLabelSx,
  sidebarDividerSx,
  withModuleChildSx,
  withModuleLeafSx,
  withModuleParentSx,
} from "./sidebarNavSx";

const SIDEBAR_OPEN_KEYS = {
  inventory: "sidebar.inventoryOpen",
  nonInventory: "sidebar.nonInventoryOpen",
  repair: "sidebar.repairOpen",
  gatepass: "sidebar.gatepassOpen",
};

const readStoredOpen = (key) => {
  try {
    return sessionStorage.getItem(key) === "true";
  } catch {
    return false;
  }
};

const writeStoredOpen = (key, value) => {
  try {
    sessionStorage.setItem(key, value ? "true" : "false");
  } catch {
    /* ignore */
  }
};

const Sidebar = ({ mobileOpen = false, onMobileClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { permissions = [], departmentAccess = null } =
    useContext(PermissionContext) || {};
  const [inventoryOpen, setInventoryOpen] = useState(
    () =>
      location.pathname.startsWith("/inventory") ||
      readStoredOpen(SIDEBAR_OPEN_KEYS.inventory)
  );
  const [nonInventoryOpen, setNonInventoryOpen] = useState(
    () =>
      location.pathname.startsWith("/non-inventory") ||
      readStoredOpen(SIDEBAR_OPEN_KEYS.nonInventory)
  );
  const [repairOpen, setRepairOpen] = useState(
    () =>
      location.pathname.startsWith("/repairs") ||
      readStoredOpen(SIDEBAR_OPEN_KEYS.repair)
  );
  const [gatepassOpen, setGatepassOpen] = useState(
    () =>
      location.pathname.startsWith("/gate-pass") ||
      readStoredOpen(SIDEBAR_OPEN_KEYS.gatepass)
  );
  const [inventoryDepartments, setInventoryDepartments] = useState([]);
  const [nonInventoryDepartments, setNonInventoryDepartments] = useState([]);
  const isMdUp = useMediaQuery("(min-width:900px)");
  const apiUrl = process.env.REACT_APP_API_URL;
  const decryptedUserName = getUsername();
  const decryptedRole = getRole();

  if (!hasSession() || !decryptedUserName || !decryptedRole) {
    redirectToLogin();
  }

  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith("/inventory")) setInventoryOpen(true);
    if (path.startsWith("/non-inventory")) setNonInventoryOpen(true);
    if (path.startsWith("/repairs")) setRepairOpen(true);
    if (path.startsWith("/gate-pass")) setGatepassOpen(true);
  }, [location.pathname]);

  useEffect(() => {
    writeStoredOpen(SIDEBAR_OPEN_KEYS.inventory, inventoryOpen);
  }, [inventoryOpen]);
  useEffect(() => {
    writeStoredOpen(SIDEBAR_OPEN_KEYS.nonInventory, nonInventoryOpen);
  }, [nonInventoryOpen]);
  useEffect(() => {
    writeStoredOpen(SIDEBAR_OPEN_KEYS.repair, repairOpen);
  }, [repairOpen]);
  useEffect(() => {
    writeStoredOpen(SIDEBAR_OPEN_KEYS.gatepass, gatepassOpen);
  }, [gatepassOpen]);

  useEffect(() => {
    if (!permissions.includes("view_department")) {
      setInventoryDepartments([]);
      setNonInventoryDepartments([]);
      return;
    }

    const fetchDepartments = async () => {
      try {
        const companyCode = getCompanyCode();
        if (!hasSession() || !companyCode) return;
        setInventoryDepartments([]);
        setNonInventoryDepartments([]);

        const [invRes, nonInvRes] = await Promise.all([
          fetch(`${apiUrl}/api/department/all/${companyCode}?lane=INVENTORY`, {
            method: "GET",
            headers: {
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
            },
          }),
          fetch(
            `${apiUrl}/api/department/all/${companyCode}?lane=NON_INVENTORY`,
            {
              method: "GET",
              headers: {
                "Cache-Control": "no-cache",
                Pragma: "no-cache",
              },
            }
          ),
        ]);

        if (invRes.ok) {
          const data = await invRes.json();
          setInventoryDepartments(Array.isArray(data) ? data : []);
        }
        if (nonInvRes.ok) {
          const data = await nonInvRes.json();
          setNonInventoryDepartments(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error("Failed to fetch departments:", err);
      }
    };

    fetchDepartments();
    window.addEventListener(DEPARTMENTS_CHANGED_EVENT, fetchDepartments);
    return () => {
      window.removeEventListener(DEPARTMENTS_CHANGED_EVENT, fetchDepartments);
    };
  }, [apiUrl, permissions]);

  useEffect(() => {
    const departments = location.pathname.startsWith("/non-inventory")
      ? nonInventoryDepartments
      : inventoryDepartments;
    if (!departments.length) return;
    const currentId = departmentIdFromLocation(location);
    if (currentId == null) return;
    const known = [...inventoryDepartments, ...nonInventoryDepartments];
    if (known.some((dept) => Number(dept.id) === currentId)) return;
    const workshop =
      departments.find(
        (dept) => String(dept.code || "").toUpperCase() === "WORKSHOP"
      ) || departments[0];
    if (!workshop || Number(workshop.id) === currentId) return;
    const params = new URLSearchParams(location.search);
    params.set("departmentId", String(workshop.id));
    navigate(
      { pathname: location.pathname, search: `?${params.toString()}` },
      { replace: true }
    );
  }, [inventoryDepartments, nonInventoryDepartments, location, navigate]);

  return (
    <Drawer
      variant={isMdUp ? "permanent" : "temporary"}
      open={isMdUp || mobileOpen}
      onClose={onMobileClose}
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          top: { xs: 56, sm: HEADER_HEIGHT },
          height: {
            xs: "calc(100vh - 56px)",
            sm: `calc(100vh - ${HEADER_HEIGHT}px)`,
          },
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          bgcolor: SIDEBAR.bg,
          border: "none",
          borderRight: "1px solid",
          borderColor: SIDEBAR.border,
        },
      }}
    >
        <List
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            paddingTop: 1,
            gap: 0,
          }}
        >
          {permissions.includes("view_product") && (
            <Box >


              <ListItemButton
                component={Link}
                to={departmentHref("/home", departmentIdFromLocation(location))}

                selected={location.pathname.startsWith("/home")}
                sx={withModuleLeafSx(CATEGORY_ACCENT.home, { py: 1, px: 2 })}
              >
                <ListItemIcon sx={categoryIconWellSx(CATEGORY_ACCENT.home)}>
                  <HomeOutlined />
                </ListItemIcon>

                <ListItemText primary="Home"
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: "medium",
                  }} />


              </ListItemButton>



            </Box>
          )}
          <Divider sx={sidebarDividerSx} />
          {hasLaneAccess(permissions, "INVENTORY") && (
            <Box>
              <ListItemButton
                selected={false}
                onClick={() => setInventoryOpen((open) => !open)}
                sx={withModuleParentSx(
                  CATEGORY_ACCENT.inventory,
                  location.pathname.startsWith("/inventory"),
                  { py: 1, px: 2 }
                )}
              >
                <ListItemIcon sx={categoryIconWellSx(CATEGORY_ACCENT.inventory)}>
                  <Inventory2OutlinedIcon />
                </ListItemIcon>

                <ListItemText
                  primary="Inventory"
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: "medium",
                  }}
                />

                {inventoryOpen ? (
                  <ExpandLess sx={{ color: SIDEBAR.muted }} />
                ) : (
                  <ExpandMore sx={{ color: SIDEBAR.muted }} />
                )}
              </ListItemButton>

              {/* SUB MENU */}
              <Collapse in={inventoryOpen} timeout="auto" unmountOnExit>
                <Box sx={expandedRailSx(CATEGORY_ACCENT.inventory)}>
                <ListItemText
                  primary="Departments"
                  sx={sectionLabelSx(CATEGORY_ACCENT.inventory)}
                />
                <DepartmentSidebarFolders
                  accent={CATEGORY_ACCENT.inventory}
                  departments={inventoryDepartments.filter((dept) =>
                    canAccessDepartmentLane(
                      departmentAccess,
                      dept.code,
                      "INVENTORY",
                      permissions
                    )
                  )}
                  lane="INVENTORY"
                  permissions={permissions}
                  departmentAccess={departmentAccess}
                  decryptedUserName={decryptedUserName}
                  location={location}
                />
                <ListItemText
                  primary="Shared"
                  sx={sectionLabelSx(CATEGORY_ACCENT.inventory)}
                />
                <List component="div" disablePadding>
                  {permissions.includes("view_min") && (
                    <ListItemButton
                      component={Link}
                      to="/inventory/fuel-issue-Note-table"
                      sx={withModuleChildSx(CATEGORY_ACCENT.inventory, { pl: 3, py: 0.75 })}
                      selected={location.pathname === "/inventory/fuel-issue-Note-table"}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <LocalGasStationIcon
                          sx={moduleIconSx(CATEGORY_ACCENT.inventory)}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary="FINs"
                        primaryTypographyProps={{
                          fontSize: 14,
                        }}
                      />
                    </ListItemButton>
                  )}
                  {permissions.includes("view_supplier") && (
                    <ListItemButton
                      component={Link}
                      to="/inventory/supplier-table"
                      sx={withModuleChildSx(CATEGORY_ACCENT.inventory, { pl: 3, py: 0.75 })}
                      selected={location.pathname === "/inventory/supplier-table"}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <LocalShipping
                          sx={moduleIconSx(CATEGORY_ACCENT.inventory)}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary="Suppliers"
                        primaryTypographyProps={{
                          fontSize: 14,
                        }}
                      />
                    </ListItemButton>
                  )}
                </List>
                </Box>
              </Collapse>
            </Box>

          )}
          <Divider sx={sidebarDividerSx} />
          {hasLaneAccess(permissions, "NON_INVENTORY") && (
            <Box>
              <ListItemButton
                selected={false}
                onClick={() => setNonInventoryOpen((open) => !open)}
                sx={withModuleParentSx(
                  CATEGORY_ACCENT.nonInventory,
                  location.pathname.startsWith("/non-inventory"),
                  { py: 1, px: 2 }
                )}
              >
                <ListItemIcon sx={categoryIconWellSx(CATEGORY_ACCENT.nonInventory)}>
                  <CategoryOutlinedIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Non Inventory"
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: "medium",
                  }}
                />
                {nonInventoryOpen ? (
                  <ExpandLess sx={{ color: SIDEBAR.muted }} />
                ) : (
                  <ExpandMore sx={{ color: SIDEBAR.muted }} />
                )}
              </ListItemButton>
              <Collapse in={nonInventoryOpen} timeout="auto" unmountOnExit>
                <Box sx={expandedRailSx(CATEGORY_ACCENT.nonInventory)}>
                <ListItemText
                  primary="Departments"
                  sx={sectionLabelSx(CATEGORY_ACCENT.nonInventory)}
                />
                <DepartmentSidebarFolders
                  accent={CATEGORY_ACCENT.nonInventory}
                  departments={nonInventoryDepartments.filter((dept) =>
                    canAccessDepartmentLane(
                      departmentAccess,
                      dept.code,
                      "NON_INVENTORY",
                      permissions
                    )
                  )}
                  lane="NON_INVENTORY"
                  permissions={permissions}
                  departmentAccess={departmentAccess}
                  decryptedUserName={decryptedUserName}
                  location={location}
                />
                </Box>
              </Collapse>
            </Box>
          )}
          <Divider sx={sidebarDividerSx} />
          {permissions.includes("view_maintenance_dispatch") && (
            <Box>
              <ListItemButton
                selected={false}
                onClick={() => setRepairOpen((open) => !open)}
                sx={withModuleParentSx(
                  CATEGORY_ACCENT.repairs,
                  location.pathname.startsWith("/repairs"),
                  { py: 1, px: 2 }
                )}
              >
                <ListItemIcon sx={categoryIconWellSx(CATEGORY_ACCENT.repairs)}>
                  <CarRepairIcon />
                </ListItemIcon>

                <ListItemText
                  primary="Repairs"
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: "medium",
                  }}
                />

                {repairOpen ? (
                  <ExpandLess sx={{ color: SIDEBAR.muted }} />
                ) : (
                  <ExpandMore sx={{ color: SIDEBAR.muted }} />
                )}
              </ListItemButton>

              {/* SUB MENU */}
              <Collapse in={repairOpen} timeout="auto" unmountOnExit>
                <Box sx={expandedRailSx(CATEGORY_ACCENT.repairs)}>
                <ListItemText
                  primary="Stock Management"
                  sx={sectionLabelSx(CATEGORY_ACCENT.repairs)}
                />
                <List component="div" disablePadding>
                  {permissions.includes("create_maintenance_dispatch") && (
                    <ListItemButton
                      component={Link}
                      to="/repairs/repairs-mrn-table"
                      sx={withModuleChildSx(CATEGORY_ACCENT.repairs, { pl: 3, py: 0.75 })}
                      selected={location.pathname === "/repairs/repairs-mrn-table"} // Make sure path is lowercase
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <NoteAltIcon
                          sx={moduleIconSx(CATEGORY_ACCENT.repairs)}
                        />
                      </ListItemIcon>

                      <ListItemText
                        primary="RMRNs"
                        primaryTypographyProps={{
                          fontSize: 14,
                        }}
                      />
                    </ListItemButton>
                  )}
                  {permissions.includes("view_po") && (
                    <ListItemButton
                      component={Link}
                      to="/repairs/repairs-po-table"
                      sx={withModuleChildSx(CATEGORY_ACCENT.repairs, { pl: 3, py: 0.75 })}
                      selected={location.pathname === "/repairs/repairs-po-table"} // Make sure path is lowercase
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <TakeoutDining
                          sx={moduleIconSx(CATEGORY_ACCENT.repairs)}
                        />
                      </ListItemIcon>

                      <ListItemText
                        primary="RPOs"
                        primaryTypographyProps={{
                          fontSize: 14,
                        }}
                      />
                    </ListItemButton>
                  )}
                  {permissions.includes("create_maintenance_dispatch") && (
                    <ListItemButton
                      component={Link}
                      to="/repairs/repairs-grn-table"
                      sx={withModuleChildSx(CATEGORY_ACCENT.repairs, { pl: 3, py: 0.75 })}
                      selected={location.pathname === "/repairs/repairs-grn-table"} // Make sure path is lowercase
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <FireTruck
                          sx={moduleIconSx(CATEGORY_ACCENT.repairs)}
                        />
                      </ListItemIcon>

                      <ListItemText
                        primary="RGRNs"
                        primaryTypographyProps={{
                          fontSize: 14,
                        }}
                      />
                    </ListItemButton>
                  )}
                  {permissions.includes("create_service_issue") && (
                    <ListItemButton
                      component={Link}
                      to="/repairs/repairs-min-table"
                      sx={withModuleChildSx(CATEGORY_ACCENT.repairs, { pl: 3, py: 0.75 })}
                      selected={location.pathname === "/repairs/repairs-min-table"} // Make sure path is lowercase
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <UpgradeIcon
                          sx={moduleIconSx(CATEGORY_ACCENT.repairs)}
                        />
                      </ListItemIcon>

                      <ListItemText
                        primary="RMINs"
                        primaryTypographyProps={{
                          fontSize: 14,
                        }}
                      />
                    </ListItemButton>
                  )}

                </List>
                <ListItemText
                  primary="Approvals"
                  sx={sectionLabelSx(CATEGORY_ACCENT.repairs)}
                />
                <List component="div" disablePadding>
                  {permissions.includes("approve_security_out") && (
                    <ListItemButton
                      component={Link}
                      to="/repairs/dispatch-approvals-table"
                      sx={withModuleChildSx(CATEGORY_ACCENT.repairs, { pl: 3, py: 0.75 })}
                      selected={location.pathname === "/repairs/dispatch-approvals-table"} // Make sure path is lowercase
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <GppGoodIcon
                          sx={moduleIconSx(CATEGORY_ACCENT.repairs)}
                        />
                      </ListItemIcon>

                      <ListItemText
                        primary="Dispatch"
                        primaryTypographyProps={{
                          fontSize: 14,
                        }}
                      />
                    </ListItemButton>
                  )}
                  {permissions.includes("approve_security_in") && (
                    <ListItemButton
                      component={Link}
                      to="/repairs/repairs-return-table"
                      sx={withModuleChildSx(CATEGORY_ACCENT.repairs, { pl: 3, py: 0.75 })}
                      selected={location.pathname === "/repairs/repairs-return-table"} // Make sure path is lowercase
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <DoneAllIcon
                          sx={moduleIconSx(CATEGORY_ACCENT.repairs)}
                        />
                      </ListItemIcon>

                      <ListItemText
                        primary="Return"
                        primaryTypographyProps={{
                          fontSize: 14,
                        }}
                      />
                    </ListItemButton>
                  )}

                </List>

                <ListItemText
                  primary="Inventory"
                  sx={sectionLabelSx(CATEGORY_ACCENT.repairs)}
                />
                <List component="div" disablePadding>
                  {permissions.includes("view_product") && (
                    <ListItemButton
                      component={Link}
                      to="/repairs/repairs-product-table"
                      sx={withModuleChildSx(CATEGORY_ACCENT.repairs, { pl: 3, py: 0.75 })}
                      selected={location.pathname === "/repairs/repairs-product-table"} // Make sure path is lowercase
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <EngineeringIcon
                          sx={moduleIconSx(CATEGORY_ACCENT.repairs)}
                        />
                      </ListItemIcon>

                      <ListItemText
                        primary="Products"
                        primaryTypographyProps={{
                          fontSize: 14,
                        }}
                      />
                    </ListItemButton>
                  )}
                  {permissions.includes("view_vehicle") && (
                    <ListItemButton
                      component={Link}
                      to="/repairs/vehicles-table"
                      sx={withModuleChildSx(CATEGORY_ACCENT.repairs, { pl: 3, py: 0.75 })}
                      selected={location.pathname === "/repairs/vehicles-table"} // Make sure path is lowercase
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CommuteIcon
                          sx={moduleIconSx(CATEGORY_ACCENT.repairs)}
                        />
                      </ListItemIcon>

                      <ListItemText
                        primary="Vehicles"
                        primaryTypographyProps={{
                          fontSize: 14,
                        }}
                      />
                    </ListItemButton>
                  )}

                </List>
                </Box>
              </Collapse>
            </Box>
          )}
          <Divider sx={sidebarDividerSx} />
          {(permissions.includes("approve_security_out") ||
            permissions.includes("approve_security_in")) && (
            <Box >
              <ListItemButton

                component={Link}
                to="/security-repairs"
                selected={location.pathname.startsWith("/security-repairs")}
                sx={withModuleLeafSx(CATEGORY_ACCENT.security, { py: 1, px: 2 })}
              >
                <ListItemIcon sx={categoryIconWellSx(CATEGORY_ACCENT.security)}>
                  <CarRepairIcon />
                </ListItemIcon>
                <ListItemText primary="Security"
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: "medium",
                  }} />
              </ListItemButton>
            </Box>
          )}
          <Divider sx={sidebarDividerSx} />

          {permissions.includes("view_maintenance_dispatch") && (
            <Box>
              <ListItemButton
                selected={false}
                onClick={() => setGatepassOpen((open) => !open)}
                sx={withModuleParentSx(
                  CATEGORY_ACCENT.gatepass,
                  location.pathname.startsWith("/gate-pass"),
                  { py: 1, px: 2 }
                )}
              >
                <ListItemIcon sx={categoryIconWellSx(CATEGORY_ACCENT.gatepass)}>
                  <CarRentalOutlinedIcon />
                </ListItemIcon>

                <ListItemText
                  primary="GatePass"
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: "medium",
                  }}
                />

                {gatepassOpen ? (
                  <ExpandLess sx={{ color: SIDEBAR.muted }} />
                ) : (
                  <ExpandMore sx={{ color: SIDEBAR.muted }} />
                )}
              </ListItemButton>

              {/* SUB MENU */}
              <Collapse in={gatepassOpen} timeout="auto" unmountOnExit>
                <Box sx={expandedRailSx(CATEGORY_ACCENT.gatepass)}>
                <ListItemText
                  primary="Gate Pass"
                  sx={sectionLabelSx(CATEGORY_ACCENT.gatepass)}
                />
                <List component="div" disablePadding>
                  {permissions.includes("view_maintenance_dispatch") && (
                    <ListItemButton
                      component={Link}
                      to="/gate-pass/dispatch-items-table"
                      sx={withModuleChildSx(CATEGORY_ACCENT.gatepass, { pl: 3, py: 0.75 })}
                      selected={location.pathname === "/gate-pass/dispatch-items-table"} // Make sure path is lowercase
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <ArrowOutwardIcon
                          sx={moduleIconSx(CATEGORY_ACCENT.gatepass)}
                        />
                      </ListItemIcon>

                      <ListItemText
                        primary="Dispatch"
                        primaryTypographyProps={{
                          fontSize: 14,
                        }}
                      />
                    </ListItemButton>
                  )}
                  {permissions.includes("view_maintenance_dispatch") && (
                    <ListItemButton
                      component={Link}
                      to="/gate-pass/return-items-table"
                      sx={withModuleChildSx(CATEGORY_ACCENT.gatepass, { pl: 3, py: 0.75 })}
                      selected={location.pathname === "/gate-pass/return-items-table"} // Make sure path is lowercase
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <KeyboardReturnOutlinedIcon
                          sx={moduleIconSx(CATEGORY_ACCENT.gatepass)}
                        />
                      </ListItemIcon>

                      <ListItemText
                        primary="Return"
                        primaryTypographyProps={{
                          fontSize: 14,
                        }}
                      />
                    </ListItemButton>
                  )}
                </List>
                </Box>
              </Collapse>
            </Box>
          )}
          <Divider sx={sidebarDividerSx} />
          {permissions.includes("view_finance") && (
            <Box>
              <ListItemButton
                component={Link}
                to="/finance-table"
                selected={location.pathname.startsWith("/finance-table")}
                sx={withModuleLeafSx(CATEGORY_ACCENT.finance, { py: 1, px: 2 })}
              >
                <ListItemIcon sx={categoryIconWellSx(CATEGORY_ACCENT.finance)}>
                  <AccountBalanceWallet />
                </ListItemIcon>

                <ListItemText
                  primary="Finance"
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: "medium",
                  }}
                />
              </ListItemButton>
            </Box>
          )}
          <Divider sx={sidebarDividerSx} />
          {permissions.includes("view_report") && (
            <ListItemButton

              component={Link}
              to={departmentHref("/reports", departmentIdFromLocation(location))}
              selected={location.pathname.startsWith("/reports")}
              sx={withModuleLeafSx(CATEGORY_ACCENT.reports, { py: 1, px: 2 })}
            >
              <ListItemIcon sx={categoryIconWellSx(CATEGORY_ACCENT.reports)}>
                <ArticleOutlinedIcon />
              </ListItemIcon>

              <ListItemText primary="Reports"
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: "medium",
                }} />

            </ListItemButton>
          )}
          <Divider sx={sidebarDividerSx} />
          {hasAnyPermission(permissions, SETTINGS_ACCESS_PERMISSIONS) && (
            <ListItemButton

              component={Link}
              to="/settings"
              selected={location.pathname.startsWith("/settings")}
              sx={withModuleLeafSx(CATEGORY_ACCENT.settings, { py: 1, px: 2 })}
            >
              <ListItemIcon sx={categoryIconWellSx(CATEGORY_ACCENT.settings)}>
                <SettingsOutlinedIcon />
              </ListItemIcon>

              <ListItemText primary="Settings"
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: "medium",
                }} />

            </ListItemButton>
          )}
          <Divider sx={sidebarDividerSx} />
        </List>
      </Drawer>
  );
};

export default Sidebar;
